/**
 * Module dependencies.
 */

/**
 * Module dependencies.
 */

import { AxiosError } from 'axios';
import { Button } from 'src/components/core/buttons/button';
import { CheckUpFormData, postCheckUp } from 'src/requests/check-up';
import { DateTimeFields } from 'src/components/forms/shared/date-time-field';
import { Form } from 'src/components/forms/shared/styles';
import { InputField } from 'src/components/core/forms/input/field';
import { NewsletterAndTermsFields } from 'src/components/forms/shared/newsletter-terms-fields';
import { PageModalLayout } from 'src/components/layout/page-modal';
import {
  PromoCodeFieldWrapper,
  promoCodeFieldName,
  useValidatePromoCode
} from './shared/promo-code-field';
import { RawHtml } from '@untile/react-components';
import { RecaptchaField } from 'src/components/core/forms/recaptcha';
import { Text } from 'src/components/core/text';
import { UserInfoFields } from 'src/components/forms/shared/user-info-fields';
import { toast } from 'src/providers/toast';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'next-i18next';
import iconButton from 'src/assets/svg/button.svg';
import styled from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  modalCloseButtonLink: string;
};

/**
 * `StyledButton` styled component.
 */

const StyledButton = styled(Button)`
  width: max-content;
`;

/**
 * Export `CheckUpForm` component.
 */

export function CheckUpForm({ modalCloseButtonLink }: Props) {
  const { t } = useTranslation(['common', 'check-up']);
  const [promoCodeMessage, setPromoCodeMessage] = useState<
    string | undefined
  >();
  const {
    clearErrors,
    control,
    formState,
    getValues,
    handleSubmit,
    reset,
    setError
  } = useForm<CheckUpFormData>();

  const [previousPromoCode, handleValidatePromoCode] = useValidatePromoCode({
    getValues,
    onError: () => {
      setError(promoCodeFieldName, { message: t('common:promoCode.error') });
    },
    onSuccess: () => {
      clearErrors(promoCodeFieldName);
      setPromoCodeMessage(t('common:promoCode.success'));
    }
  });

  const onSubmit = useCallback(
    async (formData: CheckUpFormData) => {
      try {
        await postCheckUp(formData);

        toast.success(t('check-up:network.success'));

        clearErrors(promoCodeFieldName);
        reset();
      } catch (error) {
        const response = (error as AxiosError<{ message: string }>)?.response;

        if (response?.data?.message === 'Invalid promotional code') {
          setError(promoCodeFieldName, {
            message: t('common:promoCode.error')
          });
        }

        toast.error(t('check-up:network.error'));
      }
    },
    [clearErrors, reset, setError, t]
  );

  return (
    <PageModalLayout
      aside={(
        <Text>
          <RawHtml>{t('check-up:intro.description')}</RawHtml>
        </Text>
      )}
      closeButtonLink={modalCloseButtonLink}
      subtitle={t('check-up:subtitle')}
      title={t('check-up:title')}
    >
      <Form onSubmit={handleSubmit(onSubmit)}>
        <UserInfoFields control={control} />

        <InputField
          as={'textarea'}
          control={control}
          label={t('common:form.labels.preConditions')}
          name={'preconditions'}
          placeholder={t('common:form.placeholders.preConditions')}
          rows={4}
        />

        <InputField
          as={'textarea'}
          control={control}
          label={t('common:form.labels.observations')}
          name={'observations'}
          placeholder={t('common:form.placeholders.observations')}
          rows={4}
        />

        <DateTimeFields control={control} />

        <PromoCodeFieldWrapper
          control={control as any}
          onClick={handleValidatePromoCode}
          previousPromoCode={previousPromoCode}
        >
          <InputField
            control={control}
            info={promoCodeMessage}
            label={t('form.labels.promoCode')}
            name={promoCodeFieldName}
            placeholder={t('form.placeholders.promoCode')}
          />
        </PromoCodeFieldWrapper>

        <NewsletterAndTermsFields control={control} />

        <RecaptchaField
          action={'checkUp'}
          control={control as any}
        />

        <StyledButton
          icon={iconButton}
          isLoading={formState.isSubmitting}
          type={'submit'}
        >
          {t('common:actions.book')}
        </StyledButton>
      </Form>
    </PageModalLayout>
  );
}
